import React, { useState, useRef, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert
} from '@mui/material';
import { Receipt as ChargesIcon, Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import * as yup from 'yup';

interface ExtraCharge {
  id?: number;
  chargeType?: string;
  label?: string;
  amount: number;
  description?: string;
  date?: string;
  notes?: string;
}

interface TripExtraChargesSectionProps {
  extraCharges: ExtraCharge[];
  loading?: boolean;
  tripCode: string;
  onAddCharge?: (chargePayload: {
    chargeType: string;
    amount: number;
    description?: string;
  }) => Promise<void>;
  onUpdateCharge?: (chargeId: number, chargePayload: {
    chargeType: string;
    amount: number;
    description?: string;
  }) => Promise<void>;
  onDeleteCharge?: (chargeId: number) => Promise<void>;
  disabled?: boolean;
}

// Create a styled TextField component that removes spinner buttons
const NoSpinnerTextField = styled(TextField)(`
  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type=number] {
    -moz-appearance: textfield;
  }
`);

const chargeTypes = [
  'TOLL',
  'DRIVER ALLOWANCE',
  'PARKING',
  'WAITING',
  'EXTRA_KM',
  'OTHER'
];

const formatChargeType = (type: string) => {
  if (!type) return 'Other';
  if (type === 'EXTRA_KM') return 'Extra Km';
  return type.charAt(0) + type.slice(1).toLowerCase();
};

// Validation schema
const chargeValidationSchema = yup.object().shape({
  notes: yup.string().when('chargeType', {
    is: 'DRIVER ALLOWANCE',
    then: (schema) => schema.trim().required('Notes are required for Driver Allowance'),
    otherwise: (schema) => schema.notRequired()
  })
});

const TripExtraChargesSection: React.FC<TripExtraChargesSectionProps> = ({
  extraCharges,
  loading = false,
  tripCode,
  onAddCharge,
  onUpdateCharge,
  onDeleteCharge,
  disabled = false
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newCharge, setNewCharge] = useState<{ chargeType: string; amount: string; notes: string; extraKm: string; rate: string }>({
    chargeType: '',
    amount: '',
    notes: '',
    extraKm: '',
    rate: ''
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const [updating, setUpdating] = useState<number | null>(null);
  const [editCharge, setEditCharge] = useState<{ chargeType: string; amount: string; notes: string; extraKm: string; rate: string }>({ chargeType: '', amount: '', notes: '', extraKm: '', rate: '' });
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [notesError, setNotesError] = useState<string>('');
  const sectionRef = useRef<HTMLDivElement>(null);
  const [shouldPreserveScroll, setShouldPreserveScroll] = useState(false);

  // Auto-calculate amount for Extra Km charges
  useEffect(() => {
    if (newCharge.chargeType === 'EXTRA_KM' && newCharge.extraKm && newCharge.rate) {
      const km = parseFloat(newCharge.extraKm);
      const ratePerKm = parseFloat(newCharge.rate);
      if (!isNaN(km) && !isNaN(ratePerKm)) {
        const calculatedAmount = (km * ratePerKm).toFixed(2);
        setNewCharge(prev => ({ ...prev, amount: calculatedAmount }));
      }
    }
  }, [newCharge.extraKm, newCharge.rate, newCharge.chargeType]);

  // Auto-calculate amount for Extra Km charges in edit mode
  useEffect(() => {
    if (editCharge.chargeType === 'EXTRA_KM' && editCharge.extraKm && editCharge.rate) {
      const km = parseFloat(editCharge.extraKm);
      const ratePerKm = parseFloat(editCharge.rate);
      if (!isNaN(km) && !isNaN(ratePerKm)) {
        const calculatedAmount = (km * ratePerKm).toFixed(2);
        setEditCharge(prev => ({ ...prev, amount: calculatedAmount }));
      }
    }
  }, [editCharge.extraKm, editCharge.rate, editCharge.chargeType]);

  useEffect(() => {
    if (shouldPreserveScroll && sectionRef.current) {
      setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setShouldPreserveScroll(false);
      }, 100);
    }
  }, [extraCharges, isAdding, shouldPreserveScroll]);

  const handleAddClick = () => {
    setIsAdding(true);
    setNewCharge({ chargeType: '', amount: '', notes: '', extraKm: '', rate: '' });
  };

  const handleCancel = () => {
    setIsAdding(false);
    setNewCharge({ chargeType: '', amount: '', notes: '', extraKm: '', rate: '' });
    setErrorMessage('');
  };

  const handleSaveNew = async () => {
    setSaving(true);
    setErrorMessage('');
    setShouldPreserveScroll(true);
    
    // Validation based on charge type
    if (!newCharge.chargeType) {
      setErrorMessage('Please select a charge type');
      setSaving(false);
      return;
    }
    
    if (newCharge.chargeType === 'EXTRA_KM') {
      if (!newCharge.extraKm || !newCharge.rate || parseFloat(newCharge.extraKm) <= 0 || parseFloat(newCharge.rate) <= 0) {
        setErrorMessage('Please enter valid Extra Km and Rate values');
        setSaving(false);
        return;
      }
    } else {
      if (!newCharge.amount || parseFloat(newCharge.amount) <= 0) {
        setErrorMessage('Please enter a valid amount');
        setSaving(false);
        return;
      }
    }

    // Validate notes field using yup
    try {
      await chargeValidationSchema.validate(newCharge, { abortEarly: false });
      setNotesError(''); // Clear error if validation passes
    } catch (validationError: any) {
      if (validationError.inner && validationError.inner.length > 0) {
        const notesErr = validationError.inner.find((err: any) => err.path === 'notes');
        if (notesErr) {
          setNotesError(notesErr.message);
          setSaving(false);
          return;
        }
      }
    }
    
    try {
      if (onAddCharge) {
        await onAddCharge({
          chargeType: newCharge.chargeType,
          amount: parseFloat(newCharge.amount),
          description: newCharge.notes
        });
        setSuccessMessage('Extra charge added successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        setIsAdding(false);
        setNewCharge({ chargeType: '', amount: '', notes: '', extraKm: '', rate: '' });
      }
    } catch (error: any) {
      setErrorMessage(error?.message || 'Failed to add extra charge');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeleteCharge = async (chargeId: number) => {
    if (!onDeleteCharge) return;
    
    try {
      setDeleting(chargeId);
      setErrorMessage('');
      
      await onDeleteCharge(chargeId);
      
      setSuccessMessage('Extra charge deleted successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      setErrorMessage(error?.message || 'Failed to delete extra charge');
    } finally {
      setDeleting(null);
    }
  };

  const handleEditCharge = (charge: ExtraCharge) => {
    if (!charge.id) return;
    
    setEditing(charge.id);
    setEditCharge({
      chargeType: charge.chargeType || charge.label || '',
      amount: charge.amount.toString(),
      notes: charge.description || '',
      extraKm: '',
      rate: ''
    });
  };

  const handleCancelEdit = () => {
    setEditing(null);
    setEditCharge({ chargeType: '', amount: '', notes: '', extraKm: '', rate: '' });
    setErrorMessage('');
  };

  const handleSaveEdit = async () => {
    if (!editing || !onUpdateCharge) return;
    
    setUpdating(editing);
    setErrorMessage('');
    setShouldPreserveScroll(true);
    
    // Validation based on charge type
    if (!editCharge.chargeType) {
      setErrorMessage('Please select a charge type');
      setUpdating(null);
      return;
    }
    
    if (editCharge.chargeType === 'EXTRA_KM') {
      if (!editCharge.extraKm || !editCharge.rate || parseFloat(editCharge.extraKm) <= 0 || parseFloat(editCharge.rate) <= 0) {
        setErrorMessage('Please enter valid Extra Km and Rate values');
        setUpdating(null);
        return;
      }
    } else {
      if (!editCharge.amount || parseFloat(editCharge.amount) <= 0) {
        setErrorMessage('Please enter a valid amount');
        setUpdating(null);
        return;
      }
    }

    // Validate notes field using yup
    try {
      await chargeValidationSchema.validate(editCharge, { abortEarly: false });
      setNotesError(''); // Clear error if validation passes
    } catch (validationError: any) {
      if (validationError.inner && validationError.inner.length > 0) {
        const notesErr = validationError.inner.find((err: any) => err.path === 'notes');
        if (notesErr) {
          setNotesError(notesErr.message);
          setUpdating(null);
          return;
        }
      }
    }
    
    try {
      await onUpdateCharge(editing, {
        chargeType: editCharge.chargeType,
        amount: parseFloat(editCharge.amount),
        description: editCharge.notes
      });
      
      setSuccessMessage('Extra charge updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setEditing(null);
      setEditCharge({ chargeType: '', amount: '', notes: '', extraKm: '', rate: '' });
    } catch (error: any) {
      setErrorMessage(error?.message || 'Failed to update extra charge');
    } finally {
      setUpdating(null);
    }
  };

  const totalExtraCharges = extraCharges.reduce((total, charge) => total + (charge.amount || 0), 0);

  const SectionHeader = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: '#464545',
        color: 'white',
        p: 2,
        borderRadius: '8px 8px 0 0'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ChargesIcon />
        <Typography variant="h6" fontWeight="bold">
          TRIP EXTRA CHARGES (₹{totalExtraCharges.toFixed(2)})
        </Typography>
      </Box>
      {!isAdding && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          sx={{
            color: 'white',
            borderColor: 'white',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
            '&:disabled': {
              color: 'rgba(255,255,255,0.5)',
              borderColor: 'rgba(255,255,255,0.3)'
            }
          }}
          onClick={handleAddClick}
          disabled={loading || disabled}
        >
          ADD CHARGE
        </Button>
      )}
    </Box>
  );

  return (
    <Card ref={sectionRef} sx={{ mb: 2, backgroundColor: '#1a1a1a', border: '1px solid #333' }}>
      <SectionHeader />
      <CardContent sx={{ backgroundColor: '#161616', color: 'white' }}>
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        )}
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
            {errorMessage}
          </Alert>
        )}

        {/* Add new charge form */}
        {isAdding && (
          <Box sx={{ mb: 3, p: 2, border: '1px solid #666', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              Add Extra Charge
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#999' }}>Charge Type</InputLabel>
                <Select
                  value={newCharge.chargeType}
                  onChange={(e) => setNewCharge(prev => ({ ...prev, chargeType: e.target.value }))}
                  sx={{
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#666' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#999' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0891b2' },
                    '& .MuiSvgIcon-root': { color: 'white' }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: '#2a2a2a',
                        color: 'white',
                        border: '1px solid #333',
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': {
                            backgroundColor: '#7c3aed'
                          }
                        }
                      }
                    }
                  }}
                >
                  {chargeTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {formatChargeType(type)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {/* Show Extra Km and Rate inputs for EXTRA_KM charge type */}
              {newCharge.chargeType === 'EXTRA_KM' ? (
                <>
                  <NoSpinnerTextField
                    label="Extra Km"
                    type="number"
                    value={newCharge.extraKm}
                    onChange={(e) => setNewCharge(prev => ({ ...prev, extraKm: e.target.value }))}
                    inputProps={{
                      step: 'any',
                      min: '0',
                      onWheel: (e) => {
                        const target = e.target as HTMLElement;
                        target.blur();
                      }
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        backgroundColor: '#1a1a1a',
                        '& fieldset': { borderColor: '#666' },
                        '&:hover fieldset': { borderColor: '#999' },
                        '&.Mui-focused fieldset': { borderColor: '#7c3aed' }
                      },
                      '& .MuiInputLabel-root': { color: '#999' }
                    }}
                  />
                  <NoSpinnerTextField
                    label="Rate per Km (₹)"
                    type="number"
                    value={newCharge.rate}
                    onChange={(e) => setNewCharge(prev => ({ ...prev, rate: e.target.value }))}
                    inputProps={{
                      step: 'any',
                      min: '0',
                      onWheel: (e) => {
                        const target = e.target as HTMLElement;
                        target.blur();
                      }
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        backgroundColor: '#1a1a1a',
                        '& fieldset': { borderColor: '#666' },
                        '&:hover fieldset': { borderColor: '#999' },
                        '&.Mui-focused fieldset': { borderColor: '#7c3aed' }
                      },
                      '& .MuiInputLabel-root': { color: '#999' }
                    }}
                  />
                  <NoSpinnerTextField
                    label="Calculated Amount (₹)"
                    type="number"
                    value={newCharge.amount}
                    InputProps={{
                      readOnly: true,
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        backgroundColor: '#2a2a2a',
                        '& fieldset': { borderColor: '#666' },
                        '&:hover fieldset': { borderColor: '#999' },
                        '&.Mui-focused fieldset': { borderColor: '#7c3aed' }
                      },
                      '& .MuiInputLabel-root': { color: '#999' }
                    }}
                  />
                </>
              ) : (
                <NoSpinnerTextField
                  label="Amount (₹)"
                  type="number"
                  value={newCharge.amount}
                  onChange={(e) => setNewCharge(prev => ({ ...prev, amount: e.target.value }))}
                  inputProps={{
                    step: 'any',
                    min: '0',
                    onWheel: (e) => {
                      const target = e.target as HTMLElement;
                      target.blur();
                    }
                  }}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      backgroundColor: '#1a1a1a',
                      '& fieldset': { borderColor: '#666' },
                      '&:hover fieldset': { borderColor: '#999' },
                      '&.Mui-focused fieldset': { borderColor: '#7c3aed' }
                    },
                    '& .MuiInputLabel-root': { color: '#999' }
                  }}
                />
              )}
            </Box>
            <TextField
              label={newCharge.chargeType === 'DRIVER ALLOWANCE' ? 'Notes *' : 'Notes'}
              value={newCharge.notes}
              onChange={(e) => {
                setNewCharge(prev => ({ ...prev, notes: e.target.value }));
                if (notesError) setNotesError(''); // Clear error on change
              }}
              multiline
              rows={3}
              fullWidth
              error={!!notesError}
              helperText={notesError}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  backgroundColor: '#1a1a1a',
                  '& fieldset': { borderColor: '#666' },
                  '&:hover fieldset': { borderColor: '#999' },
                  '&.Mui-focused fieldset': { borderColor: '#7c3aed' }
                },
                '& .MuiInputLabel-root': { color: '#999' },
                '& .MuiFormHelperText-root': { color: '#ef4444' }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{ color: 'white', borderColor: 'white' }}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveNew}
                sx={{ backgroundColor: '#7c3aed' }}
                disabled={
                  saving || 
                  !newCharge.chargeType || 
                  (newCharge.chargeType === 'EXTRA_KM' ? (!newCharge.extraKm || !newCharge.rate) : !newCharge.amount) ||
                  disabled
                }
              >
                {saving ? <CircularProgress size={20} /> : 'Add Charge'}
              </Button>
            </Box>
          </Box>
        )}

        {/* Display existing charges */}
        {extraCharges && extraCharges.length > 0 ? (
          <Box sx={{ mt: 2 }}>
            {/* Table Header */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 2fr 1.5fr',
                gap: 2,
                p: 2,
                backgroundColor: '#333',
                borderRadius: '4px 4px 0 0',
                borderBottom: '1px solid #555'
              }}
            >
              <Typography variant="body2" fontWeight="bold" color="#ccc">
                Charge Type
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="#ccc">
                Amount
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="#ccc">
                Notes
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="#ccc" align="right">
                Actions
              </Typography>
            </Box>
            
            {/* Table Rows */}
            {extraCharges.map((charge, index) => (
              <Box
                key={charge.id || index}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 2fr 1.5fr',
                  gap: 2,
                  p: 2,
                  backgroundColor: index % 2 === 0 ? '#2a2a2a' : '#333',
                  borderBottom: index < extraCharges.length - 1 ? '1px solid #555' : 'none',
                  '&:last-child': {
                    borderRadius: '0 0 4px 4px'
                  }
                }}
              >
                {editing === charge.id ? (
                  <>
                    {/* Edit Mode */}
                    <FormControl size="small" fullWidth>
                      <Select
                        value={editCharge.chargeType}
                        onChange={(e) => setEditCharge(prev => ({ ...prev, chargeType: e.target.value }))}
                        sx={{
                          color: 'white',
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#666' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#999' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0891b2' },
                          '& .MuiSvgIcon-root': { color: 'white' }
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              backgroundColor: '#2a2a2a',
                              color: 'white',
                              border: '1px solid #333',
                              '& .MuiMenuItem-root': {
                                color: 'white',
                                '&:hover': { backgroundColor: '#7c3aed' }
                              }
                            }
                          }
                        }}
                      >
                        {chargeTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            {formatChargeType(type)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                    {editCharge.chargeType === 'EXTRA_KM' ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <NoSpinnerTextField
                          placeholder="Extra Km"
                          type="number"
                          size="small"
                          value={editCharge.extraKm}
                          onChange={(e) => setEditCharge(prev => ({ ...prev, extraKm: e.target.value }))}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              color: 'white',
                              backgroundColor: '#1a1a1a',
                              '& fieldset': { borderColor: '#666' },
                              '&:hover fieldset': { borderColor: '#999' },
                              '&.Mui-focused fieldset': { borderColor: '#7c3aed' }
                            }
                          }}
                        />
                        <NoSpinnerTextField
                          placeholder="Rate/Km"
                          type="number"
                          size="small"
                          value={editCharge.rate}
                          onChange={(e) => setEditCharge(prev => ({ ...prev, rate: e.target.value }))}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              color: 'white',
                              backgroundColor: '#1a1a1a',
                              '& fieldset': { borderColor: '#666' },
                              '&:hover fieldset': { borderColor: '#999' },
                              '&.Mui-focused fieldset': { borderColor: '#7c3aed' }
                            }
                          }}
                        />
                        <NoSpinnerTextField
                          placeholder="Amount"
                          type="number"
                          size="small"
                          value={editCharge.amount}
                          InputProps={{ readOnly: true }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              color: 'white',
                              backgroundColor: '#2a2a2a',
                              '& fieldset': { borderColor: '#666' }
                            }
                          }}
                        />
                      </Box>
                    ) : (
                      <NoSpinnerTextField
                        placeholder="Amount"
                        type="number"
                        size="small"
                        value={editCharge.amount}
                        onChange={(e) => setEditCharge(prev => ({ ...prev, amount: e.target.value }))}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: 'white',
                            backgroundColor: '#1a1a1a',
                            '& fieldset': { borderColor: '#666' },
                            '&:hover fieldset': { borderColor: '#999' },
                            '&.Mui-focused fieldset': { borderColor: '#7c3aed' }
                          }
                        }}
                      />
                    )}
                    
                    <TextField
                      placeholder={editCharge.chargeType === 'DRIVER ALLOWANCE' ? 'Notes *' : 'Notes'}
                      size="small"
                      value={editCharge.notes}
                      onChange={(e) => {
                        setEditCharge(prev => ({ ...prev, notes: e.target.value }));
                        if (notesError) setNotesError(''); // Clear error on change
                      }}
                      multiline
                      rows={2}
                      error={!!notesError}
                      helperText={notesError}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: 'white',
                          backgroundColor: '#1a1a1a',
                          '& fieldset': { borderColor: '#666' },
                          '&:hover fieldset': { borderColor: '#999' },
                          '&.Mui-focused fieldset': { borderColor: '#7c3aed' }
                        },
                        '& .MuiFormHelperText-root': { color: '#ef4444' }
                      }}
                    />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CancelIcon />}
                        onClick={handleCancelEdit}
                        disabled={updating !== null}
                        sx={{
                          color: 'white',
                          borderColor: 'white',
                          minWidth: '32px',
                          padding: '4px 8px'
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={updating === charge.id ? <CircularProgress size={16} /> : <SaveIcon />}
                        onClick={handleSaveEdit}
                        disabled={updating !== null || !editCharge.chargeType || (editCharge.chargeType === 'EXTRA_KM' ? (!editCharge.extraKm || !editCharge.rate) : !editCharge.amount)}
                        sx={{
                          backgroundColor: '#7c3aed',
                          minWidth: '32px',
                          padding: '4px 8px'
                        }}
                      >
                        {updating === charge.id ? 'Saving...' : 'Save'}
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    {/* View Mode */}
                    <Typography variant="body2" color="white">
                      {formatChargeType(charge.label || charge.chargeType || 'OTHER')}
                    </Typography>
                    <Typography variant="body2" color="white" fontWeight="medium">
                      ₹{charge.amount?.toFixed(2) || '0.00'}
                    </Typography>
                    <Typography variant="body2" color="white">
                      {charge.description || '-'}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleEditCharge(charge)}
                        disabled={disabled || editing !== null || deleting !== null || !charge.id || !onUpdateCharge}
                        sx={{
                          color: 'white',
                          borderColor: 'white',
                          minWidth: '32px',
                          padding: '4px 8px',
                          '&:disabled': { opacity: 0.5 }
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        startIcon={deleting === charge.id ? <CircularProgress size={16} color="error" /> : <DeleteIcon />}
                        onClick={() => charge.id && handleDeleteCharge(charge.id)}
                        disabled={disabled || deleting !== null || editing !== null || !charge.id || !onDeleteCharge}
                        sx={{
                          minWidth: '32px',
                          padding: '4px 8px',
                          '&:disabled': { opacity: 0.5 }
                        }}
                      >
                        {deleting === charge.id ? 'Deleting...' : 'Delete'}
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            ))}
          </Box>
        ) : !isAdding ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="textSecondary">
              No extra charges found for this trip
            </Typography>
          </Box>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default TripExtraChargesSection;
